import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LanguageService } from '../../../services/i18n/language';
import { DEFAULT_INDUSTRY_ID, RegistrationService } from '../../../services/api/registration';
import { AuthService } from '../../../services/auth/auth';
import { DictionaryService, CountryDto } from '../../../services/api/dictionary';
import { CountrySelectComponent } from '../../shared/country-select/country-select';
import { Subscription } from 'rxjs';

/**
 * Апгрейд текущего (публичного) пользователя до грузовладельца — прямо на месте,
 * без релогина: тот же аккаунт получает роль + компанию, старые расчёты остаются доступны.
 */
@Component({
  selector: 'app-upgrade-to-cargo-owner-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CountrySelectComponent],
  templateUrl: './upgrade-to-cargo-owner-modal.html',
  styleUrl: './upgrade-to-cargo-owner-modal.scss'
})
export class UpgradeToCargoOwnerModalComponent implements OnInit, OnDestroy {
  @Output() upgraded = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  form: FormGroup;
  countries: CountryDto[] = [];
  submitting = false;
  error: string | null = null;

  titleText = 'Данные компании';
  hintText = 'Заполните данные компании и задайте пароль — все ваши расчёты сохранятся.';
  emailLabel = 'Email';
  passwordLabel = 'Пароль';
  phoneLabel = 'Телефон';
  fullNameLabel = 'ФИО';
  fullNamePlaceholderText = 'Имя Фамилия';
  confirmPasswordLabel = 'Повторите пароль';
  confirmPasswordPlaceholderText = 'Повторите пароль';
  errorPasswordsMismatchText = 'Пароли не совпадают';
  companyNameLabel = 'Название компании';
  countryLabel = 'Страна';
  selectCountryText = 'Выберите страну';
  cancelText = 'Отмена';
  continueText = 'Продолжить';
  savingText = 'Сохранение...';
  errorAutoLoginFailedText = 'Данные компании сохранены, но не удалось войти автоматически. Войдите заново с новым email.';
  errorSaveDefaultText = 'Не удалось оформить данные компании';
  passwordPlaceholderText = 'Не менее 6 символов';
  companyNamePlaceholderText = 'ТОО Рога и копыта';

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private languageService: LanguageService,
    private registrationService: RegistrationService,
    private authService: AuthService,
    private dictionaryService: DictionaryService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
      fullName: ['', [Validators.required, Validators.maxLength(255)]],
      phone: ['', [Validators.maxLength(20)]],
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(255)]],
      confirmPassword: ['', [Validators.required]],
      companyName: ['', [Validators.required, Validators.maxLength(255)]],
      countryId: [null, Validators.required]
    });
  }

  ngOnInit(): void {
    this.lockPageScroll(true);
    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );

    // ФИО и телефон уже есть в аккаунте — подставляем, клиент может поправить.
    // Вместо имени у клиента по телефону бывает сам номер или «Гость» — такое за ФИО не берём.
    const user = this.authService.getUser();
    const name = (user?.fullName ?? '').trim();
    const nameIsPhone = name.replace(/\D/g, '').length >= 10 && !/[A-Za-zА-Яа-яЁёӘәҒғҚқҢңӨөҰұҮүҺһІі]/.test(name);
    this.form.patchValue({
      fullName: name && !nameIsPhone && name !== 'Гость' ? name : '',
      phone: user?.phone || ''
    });
    // Гость приходит сюда без телефона — без номера менеджеру некуда звонить, поле обязательное
    if (this.authService.isGuest()) {
      const phone = this.form.get('phone');
      phone?.setValidators([Validators.required, Validators.pattern(/^\+?[\d\s()-]{10,20}$/)]);
      phone?.updateValueAndValidity({ emitEvent: false });
    }

    this.dictionaryService.getAllCountries().subscribe({
      next: (countries) => {
        this.countries = countries;
        // Казахстан выбран по умолчанию, пользователь может сменить
        const kazakhstan = countries.find(c => c.code === 'KZ');
        if (kazakhstan && !this.form.get('countryId')?.value) {
          this.form.patchValue({ countryId: kazakhstan.id });
        }
        this.cdr.markForCheck();
      },
      error: (err) => console.error('Error loading countries:', err)
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.lockPageScroll(false);
  }

  /** Пока окно открыто, страница под ним не прокручивается — колесо мыши листает форму. */
  private lockPageScroll(lock: boolean): void {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = lock ? 'hidden' : '';
  }

  private t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  private updateTranslations(): void {
    this.titleText = this.t('public.upgradeToCargoOwner.title', 'Данные компании');
    this.hintText = this.t('public.upgradeToCargoOwner.hint', 'Заполните данные компании и задайте пароль — все ваши расчёты сохранятся.');
    this.emailLabel = this.t('registration.cargoOwner.email', 'Email');
    this.passwordLabel = this.t('registration.cargoOwner.passwordField', 'Пароль');
    this.phoneLabel = this.t('registration.cargoOwner.phone', 'Телефон');
    this.fullNameLabel = this.t('public.upgradeToCargoOwner.fullName', 'ФИО');
    this.fullNamePlaceholderText = this.t('registration.cargoOwner.fullNamePlaceholder', 'Имя Фамилия');
    this.confirmPasswordLabel = this.t('registration.cargoOwner.confirmPassword', 'Повторите пароль');
    this.confirmPasswordPlaceholderText = this.t('registration.cargoOwner.confirmPasswordPlaceholder', 'Повторите пароль');
    this.errorPasswordsMismatchText = this.t('registration.cargoOwner.errors.passwordMismatch', 'Пароли не совпадают');
    this.companyNameLabel = this.t('registration.cargoOwner.companyName', 'Название компании');
    this.countryLabel = this.t('registration.cargoOwner.country', 'Страна');
    this.selectCountryText = this.t('registration.cargoOwner.selectCountry', 'Выберите страну');
    this.cancelText = this.t('common.cancel', 'Отмена');
    this.continueText = this.t('public.upgradeToCargoOwner.continue', 'Продолжить');
    this.savingText = this.t('public.upgradeToCargoOwner.saving', 'Сохранение...');
    this.errorAutoLoginFailedText = this.t('public.upgradeToCargoOwner.errorAutoLoginFailed', 'Данные компании сохранены, но не удалось войти автоматически. Войдите заново с новым email.');
    this.errorSaveDefaultText = this.t('public.upgradeToCargoOwner.errorSaveDefault', 'Не удалось оформить данные компании');
    this.passwordPlaceholderText = this.t('registration.cargoOwner.passwordPlaceholder', 'Минимум 6 символов');
    this.companyNamePlaceholderText = this.t('registration.cargoOwner.companyNamePlaceholder', 'ИП/ТОО «Наименование»');
  }

  getCountryName(country: CountryDto): string {
    return country.name || country.nameRu;
  }


  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.error = this.t('public.upgradeToCargoOwner.fillRequired', 'Заполните поля, выделенные красным');
      return;
    }
    if (this.form.value.password !== this.form.value.confirmPassword) {
      this.error = this.errorPasswordsMismatchText;
      return;
    }
    this.submitting = true;
    this.error = null;

    const value = this.form.value;
    const email = value.email.trim();
    const password = value.password;

    this.registrationService.upgradeToCargoOwner({
      email,
      password,
      fullName: value.fullName.trim(),
      phone: value.phone?.trim() || undefined,
      companyName: value.companyName.trim(),
      industryId: DEFAULT_INDUSTRY_ID,
      countryId: value.countryId
    }).subscribe({
      next: () => {
        // Логин сменился с телефона на email — старый токен ссылается на старый email
        // (это его "username"-claim), по нему пользователя в БД уже не найти. Токен
        // нужно переиздать обычным sign-in с новыми email/паролем (они у нас уже есть).
        this.authService.signIn({ username: email, password }).subscribe({
          next: () => {
            this.submitting = false;
            this.upgraded.emit();
          },
          error: () => {
            this.submitting = false;
            this.error = this.errorAutoLoginFailedText;
            this.cdr.markForCheck();
          }
        });
      },
      error: (err) => {
        this.submitting = false;
        this.error = err.error?.message || this.errorSaveDefaultText;
        this.cdr.markForCheck();
      }
    });
  }

  onCancel(): void {
    if (this.submitting) return;
    this.cancel.emit();
  }
}
